import React, { useEffect, useState } from "react";
import {
  AddButton,
  CancelButton,
} from "../../../components/mui/Buttons/Buttons";
import { FieldArray, Form, FormikProvider, useFormik } from "formik";
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GlobalDialog from "../../../components/mui/Dialogue/GlobalDialog";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  addSubCategory,
  deleteSubcategory,
  getSubcategories,
  updateSubcategory,
} from "../../../redux/features/assetManagementSlice";
import Loader3D from "../../../components/Loader/Loader3D";
import { Icon } from "@iconify/react";
import DeleteSubCat from "./DeleteSubCategory";

const SubCategories = ({ dispatchParams, rowData }) => {
  const [open, setOpen] = useState(false);
  const [editableSubcategories, setEditableSubcategories] = useState([]);

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { loading, subCategoryData } = useSelector(
    (state) => state.assetmanagement
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      category_id: rowData.category_id,
      sub_category: [],
    },
    onSubmit: (values) => {
      values.sub_category.forEach((item) => {
        const payload = {
          category_id: values.category_id,
          sub_category_name: item.sub_category_name,
          sub_category_prefix: item.sub_category_prefix,
          min_stock_quantity: item.min_stock_quantity,
        };
        dispatch(addSubCategory(payload)).then((res) => {
          if (res.payload.status === "success") {
            enqueueSnackbar(res.payload.message, { variant: "success" });
            dispatch(getSubcategories(rowData.category_id)).then((res) =>
              setEditableSubcategories(res.payload.data)
            );
            formik.resetForm();
          } else {
            enqueueSnackbar(res.payload.message, { variant: "error" });
          }
        });
      });
    },
  });

  const handleUpdate = (subCat) => {
    const payload = {
      category_id: rowData.category_id,
      sub_category_id: subCat.sub_category_id,
      sub_category_name: subCat.sub_category_name,
      sub_category_prefix: subCat.sub_category_prefix === "" ? null : subCat.sub_category_prefix,
      min_stock_quantity: subCat.min_stock_quantity || 0,
    };

    dispatch(updateSubcategory(payload)).then((res) => {
      if (res.payload.status === "success") {
        enqueueSnackbar(res.payload.message, { variant: "success" });
        dispatch(getSubcategories(rowData.category_id)).then((res) =>
          setEditableSubcategories(res.payload.data)
        );
      } else {
        enqueueSnackbar(res.payload.message, { variant: "error" });
      }
    });
  };

  const handledelete = (subCatID) => {
    dispatch(deleteSubcategory(subCatID)).then((res) => {
      if (res.payload.status === "success") {
        enqueueSnackbar(res.payload.message, { variant: "success" });
        dispatch(getSubcategories(rowData.category_id)).then((res) =>
          setEditableSubcategories(res.payload.data)
        );
      } else {
        enqueueSnackbar(res.payload.message, { variant: "error" });
      }
    });
  };

  useEffect(() => {
    if (open) {
      dispatch(getSubcategories(rowData.category_id)).then((res) => {
        if (res?.payload?.status === "success") {
          setEditableSubcategories(res.payload.data);
        }
      });
    }
  }, [open]);

  return (
    <GlobalDialog
      title={"Sub Category"}
      open={open}
      setOpen={setOpen}
      fullWidth
      maxWidth={"md"}
      closeButton={true}
      popupButton={
        <AddButton size="medium" action={() => setOpen(true)}>
          Add
        </AddButton>
      }
      content={
        <>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    bgcolor: "rgba(255,255,255,0.6)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10,
                  }}
                >
                  <Loader3D />
                </Box>
              )}
              <Box>
                <Stack direction={"column"} spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Chip label={rowData.category_name} color="primary" />
                  </Box>

                  {/* Existing Subcategories */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      border: "1px dashed #ccc",
                      borderRadius: "8px",
                      padding: "16px",
                      mt: 2,
                    }}
                  >
                    <Typography variant="subtitle2" color="primary">
                      Existing Sub Categories
                    </Typography>
                    {editableSubcategories.length === 0 && (
                      <Alert severity="info">No Sub Categories Added Yet</Alert>
                    )}
                    {editableSubcategories.map((sub, index) => (
                      <Stack
                        key={sub.sub_category_id}
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={1}
                      >
                        <TextField
                          label="Sub Category"
                          value={sub.sub_category_name}
                          onChange={(e) => {
                            const updated = [...editableSubcategories];
                            updated[index] = {
                              ...updated[index],
                              sub_category_name: e.target.value,
                            };
                            setEditableSubcategories(updated);
                          }}
                          fullWidth
                        />
                        <TextField
                          label="Prefix"
                          value={sub.sub_category_prefix}
                          onChange={(e) => {
                            const updated = [...editableSubcategories];
                            updated[index] = {
                              ...updated[index],
                              sub_category_prefix: e.target.value.toUpperCase(),
                            };
                            setEditableSubcategories(updated);
                          }}
                          fullWidth
                        />
                        <TextField
                          label="Minimum Stock Quantity"
                          value={sub.min_stock_quantity}
                          onChange={(e) => {
                            const updated = [...editableSubcategories];
                            updated[index] = {
                              ...updated[index],
                              min_stock_quantity: e.target.value,
                            };
                            setEditableSubcategories(updated);
                          }}
                          fullWidth
                        />
                        {/* <DeleteSubCat
                          id={sub.sub_category_id}
                          data={rowData.category_id}
                        /> */}
                        <Button
                          sx={{
                            width: "auto",
                            height: "30px",
                            backgroundColor: "#f02d3a",
                            color: "#fff",
                            py: "8px",
                            "&:hover": {
                              backgroundColor: "#800016",
                            },
                          }}
                          onClick={() => handledelete(sub.sub_category_id)}
                        >
                          Delete
                        </Button>
                        <Button
                          sx={{
                            width: "auto",
                            height: "30px",
                            backgroundColor: "#348AA7",
                            color: "#fff",
                            py: "8px",
                            "&:hover": {
                              backgroundColor: "#014f86",
                            },
                          }}
                          onClick={() => handleUpdate(sub)}
                        >
                          Update
                        </Button>
                      </Stack>
                    ))}
                  </Box>

                  {/* Add New Subcategories */}
                  <FieldArray name="sub_category">
                    {({ push, remove }) => (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          border: "1px dotted rgb(129, 156, 164)",
                          borderRadius: "8px",
                          padding: "16px",
                          marginTop: 2,
                        }}
                      >
                        <Typography variant="subtitle2" color="primary">
                          Add New Sub Categories
                        </Typography>

                        {formik.values.sub_category.map((_, index) => (
                          <Stack
                            key={index}
                            direction={{
                              xs: "column",
                              sm: "row",
                            }}
                            spacing={1}
                          >
                            <TextField
                              name={`sub_category[${index}].sub_category_name`}
                              value={
                                formik.values.sub_category[index]
                                  .sub_category_name
                              }
                              onChange={formik.handleChange}
                              label="Sub Category"
                              fullWidth
                            />
                            <TextField
                              name={`sub_category[${index}].sub_category_prefix`}
                              value={
                                formik.values.sub_category[index]
                                  .sub_category_prefix
                              }
                              onChange={(e) => {
                                const uppercaseValue =
                                  e.target.value.toUpperCase();
                                formik.setFieldValue(
                                  `sub_category[${index}].sub_category_prefix`,
                                  uppercaseValue
                                );
                              }}
                              label="Prefix"
                              fullWidth
                            />
                            <TextField
                              name={`sub_category[${index}].min_stock_quantity`}
                              value={
                                formik.values.sub_category[index]
                                  .min_stock_quantity
                              }
                              onChange={formik.handleChange}
                              label="Minimum Stock Quantity"
                              fullWidth
                            />
                            <Button
                              onClick={() => remove(index)}
                              variant="outlined"
                              color="error"
                            >
                              <Icon
                                icon={"material-symbols:delete"}
                                width={20}
                              />
                            </Button>
                          </Stack>
                        ))}

                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() =>
                            push({
                              sub_category_name: "",
                              sub_category_prefix: null,
                              min_stock_quantity: 0,
                            })
                          }
                          startIcon={<Icon icon="mdi:plus" />}
                        >
                          Add
                        </Button>
                      </Box>
                    )}
                  </FieldArray>
                </Stack>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 2,
                  }}
                >
                  <CancelButton size="small" action={() => setOpen(false)}>
                    Cancel
                  </CancelButton>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="small"
                  >
                    Submit New
                  </Button>
                </Box>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default SubCategories;
